import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Phone, Send, ArrowRight } from "lucide-react";
import { CheckCircle } from "lucide-react";

const CONTACT_PHONE = process.env.REACT_APP_CONTACT_PHONE;
const API_URL = window.location.href.includes("localhost")
  ? process.env.REACT_APP_API_URL_LOCAL
  : process.env.REACT_APP_API_URL_PROD;

const ContactForm = ({ compact = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    propertyType: "",
    budget: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);
  const validatePhone = (phone) => /^\d{10}$/.test(phone);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert("Name is required");
      return;
    }
    if (!validatePhone(formData.phone)) {
      alert("Enter a valid 10-digit phone number");
      return;
    }
    if (!validateEmail(formData.email)) {
      alert("Enter a valid email address");
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_URL}api/global-form/skyline-properties/inquiry-customers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to submit form");

      setSuccessMessage(data.message || "Inquiry submitted successfully!");
      setSubmitted(true); // Hide form and show success
    } catch (err) {
      console.error(err);
      alert(err.message || "Error submitting form");
    } finally {
      setLoading(false);
    }
  };

  // Compact version
  if (compact) {
    if (submitted) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="mx-auto text-green-500 w-12 h-12 mb-4" />
          <p className="text-green-600 font-semibold">{successMessage}</p>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <h4 className="font-display text-lg text-primary mb-4">Quick Registration</h4>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Full Name"
            required
          />
          <Input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="Phone Number"
            required
          />
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="Email Address"
            required
          />
          <Button type="submit" className="w-full btn-luxury" disabled={loading}>
            {loading ? "Submitting..." : "Register Interest"}
          </Button>
        </form>
      </div>
    );
  }

  // Full version
  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-neutral-50 to-white">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display-bold text-4xl md:text-5xl mb-6 text-primary">
              Contact Us - Professional Service
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Get personalized assistance from our real estate experts. We're here to help you find the perfect property investment opportunity.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-8 animate-fade-up">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Phone className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <div className="font-medium text-primary">Call Us Directly</div>
                  <div className="font-medium text-primary"><b>CEO: Shiva Chilveri</b></div>
                  <div className="text-muted-foreground">{CONTACT_PHONE}</div>
                  <div className="text-muted-foreground text-sm mb-2">Available 10 AM - 9 PM</div>

                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-green-400 to-teal-500 text-white hover:from-teal-500 hover:to-green-400 border-0 shadow-md transition-all duration-300 mt-2"
                    onClick={() => window.open(`tel:${CONTACT_PHONE}`)}
                  >
                    Call Now
                  </Button>
                </div>
              </div>

              <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
                <h3 className="font-display text-xl mb-4 text-primary">Why Choose Skyline Properties?</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• 15+ years of trusted service in Solapur</li>
                  <li>• 20,000+ satisfied customers</li>
                  <li>• Legal documentation assistance</li>
                  <li>• Free property consultation</li>
                  <li>• Post-sale support & maintenance</li>
                </ul>
              </div>
            </div>

            <div className="bg-white border border-neutral/10 shadow-lg rounded-2xl p-8 animate-fade-up" style={{ animationDelay: "0.3s", minHeight: "500px" }}>
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <CheckCircle className="text-green-500 w-16 h-16 mb-4 animate-bounce" />
                  <p className="text-green-600 font-semibold text-lg text-center">{successMessage}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6 h-full">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-primary">Full Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-2"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-primary">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="mt-2"
                        placeholder="Your contact number"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-primary">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-2"
                      placeholder="Your email address"
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="propertyType" className="text-primary">Property Type</Label>
                      <Select value={formData.propertyType} onValueChange={(value) => setFormData({ ...formData, propertyType: value })}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select property type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="apartment">Apartment</SelectItem>
                          <SelectItem value="bungalow">Bungalow</SelectItem>
                          <SelectItem value="plot">Plot/Land</SelectItem>
                          <SelectItem value="commercial">Commercial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="budget" className="text-primary">Budget Range</Label>
                      <Select value={formData.budget} onValueChange={(value) => setFormData({ ...formData, budget: value })}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select budget" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="under-50">Under ₹50 Lakhs</SelectItem>
                          <SelectItem value="50-100">₹50 - ₹1 Crore</SelectItem>
                          <SelectItem value="100-200">₹1 - ₹2 Crores</SelectItem>
                          <SelectItem value="above-200">Above ₹2 Crores</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-primary">Additional Requirements</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="mt-2"
                      placeholder="Tell us about your specific requirements, preferred location, timeline, etc."
                      rows={4}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full btn-luxury font-medium py-3 group"
                    disabled={loading}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    {loading ? "Submitting..." : "Send Inquiry"}
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>

                  <p className="text-center text-muted-foreground text-sm">
                    By submitting this form, you agree to our privacy policy and terms of service.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

export default ContactForm;
